import { useMemo, useState, type ReactNode } from "react";
import {
  CheckCircle2,
  CircleDot,
  History,
  MessageSquarePlus,
  Pencil,
  RotateCcw,
  Trash2,
} from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { isOpenStatus } from "@/domains/pm/statuses/statusCatalogue";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import type { IssueStatus, PMIssueActivity } from "@/domains/pm/types";
import { formatCommentTimestamp } from "@/domains/pm/utils/panelDates";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useIssueActivityQuery, useThreadActivityQuery } from "@/stores/documents/comments/queries";
import type {
  DocumentThreadActionType,
  DocumentThreadActivity,
} from "@/stores/documents/comments/types";
import { timestampLabels } from "./commentsLabels";

const ACTIVITY_ICON: Record<DocumentThreadActionType, ReactNode> = {
  thread_created: <MessageSquarePlus className="h-3.5 w-3.5" />,
  comment_added: <MessageSquarePlus className="h-3.5 w-3.5" />,
  comment_edited: <Pencil className="h-3.5 w-3.5" />,
  comment_deleted: <Trash2 className="h-3.5 w-3.5" />,
  resolved: <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />,
  reopened: <RotateCcw className="h-3.5 w-3.5" />,
  issue_created: <CircleDot className="h-3.5 w-3.5" />,
  thread_deleted: <Trash2 className="h-3.5 w-3.5" />,
};

interface ThreadHistoryPopoverProps {
  threadId: number;
  /** The sub-issue of an issue-backed thread — its activity log IS the history. */
  issueId: number | null;
  projectId: number | null;
  /** The trigger element (a menu item or icon button). */
  children: ReactNode;
}

/**
 * "Mark as complete (with history)": every completion, reopening, edit and
 * deletion on the thread, with who did it and when. An opted-out thread reads
 * its own trigger-written log; an issue-backed one reads the sub-issue's
 * `pm_issue_activity`, so the Issues page and this popover never disagree.
 */
export function ThreadHistoryPopover({
  threadId,
  issueId,
  projectId,
  children,
}: ThreadHistoryPopoverProps) {
  const { t, language } = useLanguage();
  const { catalogue } = useStatusCatalogue();
  const [open, setOpen] = useState(false);
  const c = t.documents.comments;
  const stamps = timestampLabels(c, language);

  const own = useThreadActivityQuery(issueId == null ? threadId : null, open && issueId == null);
  const issue = useIssueActivityQuery(issueId, open && issueId != null);

  const issueLines = useMemo<DocumentThreadActivity[]>(() => {
    const rows = (issue.data ?? []) as PMIssueActivity[];
    const out: DocumentThreadActivity[] = [];
    for (const row of rows) {
      let actionType: DocumentThreadActionType | null = null;
      switch (row.action_type) {
        case "issue_created":
          actionType = "thread_created";
          break;
        case "comment_added":
          actionType = "comment_added";
          break;
        case "comment_edited":
          actionType = "comment_edited";
          break;
        case "issue_deleted":
          actionType = "thread_deleted";
          break;
        case "status_changed": {
          const next = typeof row.new_value === "string" ? (row.new_value as IssueStatus) : null;
          actionType = next && isOpenStatus(catalogue, projectId, next) ? "reopened" : "resolved";
          break;
        }
        default:
          actionType = null;
      }
      if (!actionType) continue;
      const actorName = row.metadata?.actor_name;
      out.push({
        id: row.id,
        threadId,
        actionType,
        actorId: row.actor_id,
        actorName: typeof actorName === "string" && actorName.trim() ? actorName : null,
        createdAt: row.created_at,
        metadata: row.metadata ?? {},
      });
    }
    return out;
  }, [issue.data, catalogue, projectId, threadId]);

  const activity = issueId == null ? own.activity : issueLines;
  const isLoading = issueId == null ? own.isLoading : issue.isLoading;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3">
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold">
          <History className="h-3.5 w-3.5" />
          {c.historyTitle}
        </div>
        {isLoading ? (
          <div className="text-xs text-muted-foreground">…</div>
        ) : activity.length === 0 ? (
          <div className="text-xs text-muted-foreground">{c.historyEmpty}</div>
        ) : (
          <ol className="space-y-2">
            {activity.map((a) => {
              const name = a.actorName ?? c.systemActor;
              const line = c.history[a.actionType].replace("{name}", name);
              const issueNumber = a.metadata.issue_number;
              return (
                <li key={a.id} className="flex items-start gap-2 text-xs">
                  <span className="mt-0.5 shrink-0 text-muted-foreground">
                    {ACTIVITY_ICON[a.actionType]}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span>
                      {line}
                      {a.actionType === "issue_created" && typeof issueNumber === "number" && (
                        <span className="text-muted-foreground"> #{issueNumber}</span>
                      )}
                    </span>
                    <span className="block text-[10px] text-muted-foreground">
                      {formatCommentTimestamp(a.createdAt, stamps)}
                    </span>
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </PopoverContent>
    </Popover>
  );
}
